// src/features/patient/care-treatment/components/VisitsTimeline.tsx

/**
 * Purpose : The Visits tab: one row per visit day in year
 * sections (sticky year headers), or the existing visit cards; the type
 * filter (each day under one type), the year filter (any number of years;
 * none picked: every year), the clinician filter and the List / Cards switch
 * (remembered in this browser). Long lists render in windows of 200 rows.
 *
 * Contents :
 *
 * * function(VisitsTimeline) : filters, view switch, list or cards
 * * function(VisitDayRow) : one visit day in the list
 * * function(VisitDayCard) : one visit day as the existing card
 * * function(readView) / function(saveView) : the remembered List / Cards choice
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { EmptyState } from "@/shared/components/EmptyState";
import { Select } from "@/shared/components/select";
import { UnderlineTabs } from "@/shared/components/UnderlineTabs";
import { cn } from "@/shared/lib/utils";
import type { CareTeamMember, Note } from "@/features/patient/care-treatment/api/careTimelineTypes";
import { formatWhen, isInferredVisit, noteChips } from "@/features/patient/care-treatment/utils/careTimeline";
import { visitKind } from "@/features/patient/care-treatment/utils/careNaming";
import {
  VISIT_DAY_FILTERS,
  dayHasDetails,
  dayMatches,
  filterOfKind,
  dayNotes,
  groupByYear,
  noteCountText,
  personKey,
  summaryLine,
  visitDayMeta,
  visitDayTitle,
  type DayContent,
  type VisitDay,
  type VisitDayFilter,
} from "@/features/patient/care-treatment/utils/visitDays";
import { KindTile, SourceBadges } from "./careParts";
import { LoadOlderButton, usePaged } from "./LoadOlder";
import {
  activeFilterClass,
  brandButtonClass,
  clickableCardClass,
  dividerClass,
  faintTextClass,
  filterButtonClass,
  greyChipClass,
  inactiveFilterClass,
  inferredNoticeClass,
  mutedIconClass,
  navyChipClass,
  staticCardClass,
  titleClass,
} from "./careStyles";

/** A visit with nothing more than its date, place and clinician (dayHasDetails). */
export const DETAILS_ONLY_TEXT = "Only the date, place and clinician were recorded for this visit. There are no notes or results to open.";

export type VisitsView = "list" | "cards";

const VIEW_KEY = "msq.careTreatment.visitsView";

// function(readView) / function(saveView)
/** The remembered List / Cards choice; List when nothing (or no storage) is there. */
export function readView(key: string = VIEW_KEY): VisitsView {
  try {
    return window.localStorage.getItem(key) === "cards" ? "cards" : "list";
  } catch {
    return "list";
  }
}

export function saveView(view: VisitsView, key: string = VIEW_KEY): void {
  try {
    window.localStorage.setItem(key, view);
  } catch {
    // Private window or blocked storage: the choice lasts for this visit only.
  }
}

const CONTENT_LABEL: Record<DayContent, string> = {
  notes: "Visits with doctors’ notes",
  results: "Visits with results & orders",
};

/** The day's sources, each file once. */
const daySources = (day: VisitDay) => day.parts.flatMap((p) => p.sources);

// function(VisitsTimeline)
/**
 * @param days - Every visit day, newest first (the filters count over these).
 * @param notes - All notes (for counts and the clinician filter).
 * @param filter / onFilter - The type filter.
 * @param clinician / onClinician - The clinician filter (a grouping key), null for all.
 * @param people - Who can be picked in the clinician filter.
 * @param onOpen - Open a visit day.
 */
export function VisitsTimeline({
  days, notes, filter, onFilter, clinician, onClinician, content = null, onClearContent, people, onOpen,
}: {
  days: VisitDay[];
  notes: Note[];
  filter: VisitDayFilter;
  onFilter: (filter: VisitDayFilter) => void;
  clinician: string | null;
  onClinician: (key: string | null) => void;
  /** From a summary card: only days with doctors' notes / results & orders. */
  content?: DayContent | null;
  onClearContent?: () => void;
  people: CareTeamMember[];
  onOpen: (day: VisitDay) => void;
}) {
  const [view, setView] = useState<VisitsView>(() => readView());
  const pickView = (next: VisitsView) => {
    setView(next);
    saveView(next);
  };

  // The year picked in the year filter; null: every year.
  const [pickedYear, setPickedYear] = useState<string | null>(null);
  const matching = useMemo(
    () => days.filter((d) => dayMatches(d, notes, filter, clinician, content)),
    [days, notes, filter, clinician, content],
  );
  // The years the other filters leave, newest first (a picked year with nothing left stays listed).
  const yearOptions = useMemo(
    () => [...new Set([...matching.map((d) => d.date.slice(0, 4)), ...(pickedYear ? [pickedYear] : [])])].sort((a, b) => b.localeCompare(a)),
    [matching, pickedYear],
  );
  const shown = useMemo(
    () => (pickedYear ? matching.filter((d) => d.date.startsWith(pickedYear)) : matching),
    [matching, pickedYear],
  );
  // 10 visit days at a time (any filter or view change starts again at 10).
  const paged = usePaged(shown, [filter, clinician ?? "", content ?? "", pickedYear ?? "", view].join("|"));
  const windowed = paged.shown;
  const years = useMemo(() => groupByYear(windowed), [windowed]);

  // Only clinicians with a visit under the other filters (type, summary card, year). The care
  // team also holds people who only wrote calls, messages or unlinked notes: listed here they
  // matched no visit ("No visits with this clinician"). The one picked always stays listed.
  const clinicianOptions = useMemo(() => {
    const withVisits = new Set<string>();
    for (const day of days) {
      if (pickedYear && !day.date.startsWith(pickedYear)) continue;
      if (!dayMatches(day, notes, filter, null, content)) continue;
      for (const part of day.parts) if (part.practitioner?.trim()) withVisits.add(personKey(part.practitioner, part.practitionerKey));
      for (const note of dayNotes(day, notes)) if (note.author?.trim()) withVisits.add(personKey(note.author, note.authorKey));
    }
    return people.filter((p) => withVisits.has(p.key) || p.key === clinician);
  }, [days, notes, filter, content, pickedYear, people, clinician]);

  // Type filters count visits (each under its own type, so they add up to All);
  // empty ones are left out, "All" always stays. Over the year picked and the
  // other filters, once per change, not per render.
  const filterTabs = useMemo(() => {
    const inYears = pickedYear ? days.filter((d) => d.date.startsWith(pickedYear)) : days;
    const counts = new Map<VisitDayFilter, number>();
    for (const day of inYears) {
      if (!dayMatches(day, notes, "all", clinician, content)) continue;
      for (const part of day.parts) {
        const id = filterOfKind(visitKind(part));
        counts.set(id, (counts.get(id) ?? 0) + 1);
        counts.set("all", (counts.get("all") ?? 0) + 1);
      }
    }
    return VISIT_DAY_FILTERS
      .map(({ id, label }) => ({ id, label, count: counts.get(id) ?? 0 }))
      .filter((t) => t.id === "all" || t.count > 0 || t.id === filter);
  }, [days, notes, clinician, content, filter, pickedYear]);

  if (days.length === 0) {
    return (
      <EmptyState
        icon={<Icon icon="lucide:calendar-days" className={mutedIconClass} />}
        title="No visits yet"
        description="Your visits, with the notes written for them, will appear here"
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Phone: the type filter stays in reach under the search bar while scrolling. */}
      <div data-testid="visits-filter-bar" className="sticky top-14 z-10 -mx-4 bg-[#F7F7F7] px-4 sm:-mx-6 sm:px-6 lg:static lg:mx-0 lg:px-0">
        <UnderlineTabs<VisitDayFilter> label="Filter visits" active={filter} onChange={onFilter} tabs={filterTabs} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-full flex-wrap gap-3 sm:w-auto">
          {yearOptions.length > 1 && (
            <div className="w-full sm:w-[280px]">
              {/* One year at a time, like the clinician filter: picking closes the list. */}
              <Select
                id="visits-years"
                aria-label="Years"
                showSearch
                optionFilterProp="label"
                value={pickedYear ?? ""}
                onChange={(value) => setPickedYear((value as string) || null)}
                options={[{ value: "", label: "All years" }, ...yearOptions.map((year) => ({ value: year, label: year }))]}
              />
            </div>
          )}
          {clinicianOptions.length > 0 && (
            <div className="w-full sm:w-[280px]">
              {/* Searchable: type part of a name instead of scrolling the list. */}
              <Select
                id="visits-clinician"
                aria-label="Clinician"
                showSearch
                optionFilterProp="label"
                value={clinician ?? ""}
                onChange={(value) => onClinician((value as string) || null)}
                options={[{ value: "", label: "All clinicians" }, ...clinicianOptions.map((p) => ({ value: p.key, label: p.name }))]}
              />
            </div>
          )}
        </div>
        {content && onClearContent && (
          <span className={cn("inline-flex items-center gap-1.5", navyChipClass, "!py-1 !text-xs")}>
            {CONTENT_LABEL[content]}
            <button type="button" onClick={onClearContent} aria-label="Show all visits" className="inline-flex rounded-full hover:text-[#E4002B]">
              <Icon icon="lucide:x" className="h-3.5 w-3.5" />
            </button>
          </span>
        )}
        <div role="group" aria-label="View" className="ml-auto flex gap-2">
          {(["list", "cards"] as const).map((v) => (
            <Button
              key={v}
              variant="outline"
              aria-pressed={view === v}
              onClick={() => pickView(v)}
              className={cn(filterButtonClass, "!h-8", view === v ? activeFilterClass : inactiveFilterClass)}
            >
              <Icon icon={v === "list" ? "lucide:list" : "lucide:layout-grid"} className="h-3.5 w-3.5" />
              {v === "list" ? "List" : "Cards"}
            </Button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <EmptyState
          icon={<Icon icon="lucide:calendar-search" className={mutedIconClass} />}
          title={
            clinician ? "No visits with this clinician"
              : content ? `No visits ${CONTENT_LABEL[content].replace(/^Visits /, "")}`
                : pickedYear ? `No visits in ${pickedYear}` : "No visits of this type yet"
          }
          description={content || pickedYear ? "Clear the filters to see every visit" : "Choose All to see every visit"}
        />
      ) : view === "cards" ? (
        <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {windowed.map((day) => <VisitDayCard key={day.id} day={day} notes={dayNotes(day, notes)} onOpen={() => onOpen(day)} />)}
        </div>
      ) : (
        <div className="min-w-0">
          <div className="min-w-0 space-y-6">
            {years.map(({ year, days: inYear }) => (
              <section key={year} aria-labelledby={`visits-year-${year}`}>
                <h2
                  id={`visits-year-${year}`}
                  className="sticky top-26 z-[5] -mx-1 scroll-mt-28 bg-[#F7F7F7] px-1 py-2 text-lg font-extrabold text-[#0A2947] lg:top-0 lg:scroll-mt-0"
                >
                  {year}
                </h2>
                <div className="grid gap-2">
                  {inYear.map((day) => (
                    <VisitDayRow key={day.id} day={day} notes={dayNotes(day, notes)} onOpen={() => onOpen(day)} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      )}

      <LoadOlderButton show={paged.hasMore} onClick={paged.more} />
    </div>
  );
}

// function(VisitDayRow)
/**
 * One visit day: date, type tile, title, clinician · department, the summary
 * line as written, note count and sources. Missing parts leave no gap.
 */
function VisitDayRow({ day, notes, onOpen }: { day: VisitDay; notes: Note[]; onOpen: () => void }) {
  const [y, m, d] = [+day.date.slice(0, 4), +day.date.slice(5, 7), +day.date.slice(8, 10)];
  const date = new Date(y, m - 1, d);
  const valid = !Number.isNaN(date.getTime());
  const meta = visitDayMeta(day);
  const summary = summaryLine(day);
  const count = noteCountText(notes);
  const opens = dayHasDetails(day, notes);
  // Nothing to open: a plain row saying so, not a button to an empty page.
  const Row = opens ? "button" : "div";

  return (
    <Row
      {...(opens ? { type: "button" as const, onClick: onOpen } : { "data-testid": "visit-details-only" })}
      className={cn(
        "flex w-full items-start gap-3 border p-3 text-left sm:gap-4 sm:p-4",
        opens ? clickableCardClass : staticCardClass,
        "!rounded-2xl",
      )}
    >
      {valid && (
        <span className="flex w-10 shrink-0 flex-col items-center text-center sm:w-12" aria-label={date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}>
          <span className="text-[11px] font-bold uppercase text-[#9CA3AF]">{date.toLocaleDateString("en-US", { month: "short" })}</span>
          <span className="text-lg font-extrabold leading-tight text-[#0A2947] sm:text-xl">{date.getDate()}</span>
          <span className="text-[11px] text-[#9CA3AF]">{date.toLocaleDateString("en-US", { weekday: "short" })}</span>
        </span>
      )}
      <KindTile kind={visitKind(day.parts[0])} />
      <span className="min-w-0 flex-1">
        <span className={cn("block text-sm sm:text-base", titleClass)}>{visitDayTitle(day)}</span>
        {meta && <span className="block truncate text-xs text-[#6B7280] sm:text-sm">{meta}</span>}
        {summary && (
          <span className="mt-1 line-clamp-2 block text-xs text-[#111827] sm:text-sm">
            <span className="font-semibold">{summary.label}: </span>
            <span>{summary.text}</span>
          </span>
        )}
        {!opens && <span className={cn("mt-1 block", faintTextClass)}>{DETAILS_ONLY_TEXT}</span>}
      </span>
      {(count || daySources(day).length > 0) && (
        <span className="flex shrink-0 flex-col items-end gap-1.5">
          {count && <span className="whitespace-nowrap text-xs text-[#6B7280]">{count}</span>}
          <span className="hidden sm:block"><SourceBadges sources={daySources(day)} /></span>
        </span>
      )}
    </Row>
  );
}

// function(VisitDayCard)
/** The existing visit card, for a visit day. */
function VisitDayCard({ day, notes, onOpen }: { day: VisitDay; notes: Note[]; onOpen: () => void }) {
  const first = day.parts[0];
  const meta = visitDayMeta(day);
  const summary = summaryLine(day);
  const when = formatWhen(first.start, day.parts.length === 1 ? first.end : undefined, first.hasTime);
  const chips = noteChips(notes);
  const count = noteCountText(notes);
  const facility = first.facility && first.facility !== first.department ? first.facility : "";
  const opens = dayHasDetails(day, notes);
  const Card = opens ? "button" : "div";

  return (
    <Card
      {...(opens ? { type: "button" as const, onClick: onOpen } : { "data-testid": "visit-details-only" })}
      className={cn("flex h-full w-full flex-col gap-3 border p-5 text-left", opens ? clickableCardClass : staticCardClass)}
    >
      <span className="flex w-full items-start gap-4">
        <KindTile kind={visitKind(first)} />
        <span className="min-w-0 flex-1">
          <span className={cn("block", titleClass)}>{visitDayTitle(day)}</span>
          {meta && <span className="block truncate text-sm font-semibold text-[#0A2947]">{meta}</span>}
          {facility && <span className="mt-1 block truncate text-sm text-[#9CA3AF]">{facility}</span>}
        </span>
      </span>

      {summary && (
        <span className="block text-sm text-[#111827]">
          <span className="font-semibold">{summary.label}: </span>
          <span>{summary.text}</span>
        </span>
      )}

      {chips.length > 0 && (
        <span className="flex flex-wrap gap-1.5" aria-label="Notes">
          {chips.map((chip) => <span key={chip} className={greyChipClass}>{chip}</span>)}
        </span>
      )}

      {isInferredVisit(notes) && (
        <span className={cn("block w-full", inferredNoticeClass)}>Notes linked by date and clinician — open to check them</span>
      )}

      {!opens && <span className={cn("block", faintTextClass)}>{DETAILS_ONLY_TEXT}</span>}

      {(when || count || daySources(day).length > 0) && (
        <span className={cn("mt-auto flex w-full flex-wrap items-center justify-between gap-2 pt-3", dividerClass)}>
          <span className="flex items-center gap-3 text-xs text-muted-foreground">
            {when && (
              <span className="flex items-center gap-1.5">
                <Icon icon="lucide:calendar-days" className="h-3.5 w-3.5" />
                {when}
              </span>
            )}
            {count && <span>{count}</span>}
          </span>
          <SourceBadges sources={daySources(day)} />
        </span>
      )}
    </Card>
  );
}
